import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiPlay, FiPause, FiHeart, FiShare2, FiDownload, FiCopy, FiLoader, FiEdit3, FiX, FiCheck, FiTwitter, FiFacebook, FiLink, FiList, FiCheckCircle, FiPlus } = FiIcons;

const translationEditions = {
    arabic: '', // Base arabic
    bangla: 'bn.bengali',
    chinese: 'zh.jian',
    english: 'en.sahih',
    farsi: 'fa.ayati',
    french: 'fr.hamidullah',
    hindi: 'hi.hindi',
    indonesian: 'id.indonesian',
    japanese: 'ja.japanese',
    russian: 'ru.kuliev',
    spanish: 'es.cortes',
    turkish: 'tr.diyanet',
    urdu: 'ur.jalandhry'
};

const fontClasses = {
    'amiri': 'font-amiri',
    'noto-sans-arabic': 'font-noto',
    'scheherazade': 'font-scheherazade',
    'uthmanic': 'font-arabic'
};

const fontSizeClasses = {
    small: 'text-base md:text-lg',
    medium: 'text-xl md:text-2xl',
    large: 'text-2xl md:text-3xl',
    xlarge: 'text-3xl md:text-4xl'
};

const QuranReader = ({ surah, isPlaying, onPlayToggle, onSurahEnded }) => {
    const { user, addFavorite, addNote, updateQuranProgress, addToPlaylist, createPlaylist } = useAuth();
    const { config } = useConfig();
    const [selectedTranslation, setSelectedTranslation] = useState(user?.preferences?.translation || 'english');
    const [fontSize, setFontSize] = useState(user?.preferences?.fontSize || 'medium');
    const [activeReciter, setActiveReciter] = useState(user?.preferences?.audio?.defaultReciter || 'mishaari_raashid_al_3afaasee');
    const [verses, setVerses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeNoteVerse, setActiveNoteVerse] = useState(null);
    const [activeShareVerse, setActiveShareVerse] = useState(null);
    const [showPlaylistModal, setShowPlaylistModal] = useState(false);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [noteContent, setNoteContent] = useState('');
    const audioRef = useRef(new Audio());

    // Update local state when user preferences change
    useEffect(() => {
        if (user?.preferences) {
            if (user.preferences.translation) setSelectedTranslation(user.preferences.translation);
            if (user.preferences.fontSize) setFontSize(user.preferences.fontSize);
            if (user.preferences.audio?.defaultReciter) setActiveReciter(user.preferences.audio.defaultReciter);
            // Update playback speed
            if (user.preferences.audio?.playbackSpeed) {
                audioRef.current.playbackRate = user.preferences.audio.playbackSpeed;
            }
        }
    }, [user]);

    // Fetch verse data when surah or translation changes
    useEffect(() => {
        const fetchSurahData = async () => {
            setLoading(true);
            try {
                // Construct API URL based on translation
                const translationId = translationEditions[selectedTranslation] || 'en.sahih';
                let apiUrl = `https://api.alquran.cloud/v1/surah/${surah.number}/editions/quran-uthmani`;

                if (selectedTranslation !== 'arabic') {
                    apiUrl += `,${translationId}`;
                }

                const response = await fetch(apiUrl);
                const data = await response.json();

                if (data.status === 'OK') {
                    const arabicData = data.data[0].ayahs;
                    let translationData = [];

                    if (data.data.length > 1) {
                        translationData = data.data[1].ayahs;
                    }

                    const combinedVerses = arabicData.map((ayah, index) => ({
                        number: ayah.numberInSurah,
                        arabic: ayah.text,
                        translation: translationData.length > 0 ? translationData[index].text : '',
                        transliteration: ''
                    }));
                    setVerses(combinedVerses);
                } else {
                    toast.error('Failed to load Surah data');
                }
            } catch (error) {
                console.error('Error fetching Surah data:', error);
                toast.error('Error loading Surah content');
            } finally {
                setLoading(false);
            }
        };

        if (surah) {
            fetchSurahData();
            // updateQuranProgress('clicked', surah.number);

            // Stop current playback briefly to load new source
            audioRef.current.pause();

            const paddedNumber = String(surah.number).padStart(3, '0');
            // Use the active reciter from preferences or local state
            const reciterId = activeReciter;
            audioRef.current.src = `https://download.quranicaudio.com/quran/${reciterId}/${paddedNumber}.mp3`;
            audioRef.current.load();
            if (user?.preferences?.audio?.playbackSpeed) {
                audioRef.current.playbackRate = user.preferences.audio.playbackSpeed;
            }

            // If parent says we should be playing, start playing the new source
            if (isPlaying) {
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.error("Audio auto-play failed:", error);
                        onPlayToggle();
                        toast.error("Could not auto-play next Surah");
                    });
                }
            }

            audioRef.current.onended = () => {
                updateQuranProgress('listened', surah.number);
                if (onSurahEnded) {
                    onSurahEnded();
                } else {
                    onPlayToggle();
                }
            };
        }

        return () => {
            // Cleanup
        };
    }, [surah, selectedTranslation, activeReciter, isPlaying, onPlayToggle, onSurahEnded, updateQuranProgress, user]);

    // Handle Audio Playback toggle from parent
    useEffect(() => {
        if (isPlaying) {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.error("Audio play failed:", error);
                    onPlayToggle();
                });
            }
        } else {
            audioRef.current.pause();
        }
    }, [isPlaying, onPlayToggle]);

    const handleFavorite = (verse) => {
        if (!user) {
            toast.error('Please sign in to favorite verses');
            return;
        }
        addFavorite({
            id: `${surah.number}-${verse.number}`,
            type: 'quran',
            surah: surah.name,
            verse: verse.number,
            text: verse.arabic,
            translation: verse.translation,
            date: new Date().toISOString()
        });
        toast.success('Added to favorites!');
    };

    const handleMarkAsRead = () => {
        if (!user) {
            toast.error('Please sign in to track progress');
            return;
        }
        updateQuranProgress('read', surah.number);
        toast.success('Surah marked as read!');
    };

    const handleAddToPlaylist = (playlistId) => {
        addToPlaylist(playlistId, surah.number);
        toast.success('Added to playlist');
        setShowPlaylistModal(false);
    };

    const handleCreateAndAddPlaylist = () => {
        if (!newPlaylistName.trim()) return;
        createPlaylist(newPlaylistName);
        setNewPlaylistName('');
        toast.success('Playlist created! Select it to add.');
    };

    const openNoteModal = (verse) => {
        if (!user) {
            toast.error('Please sign in to add notes');
            return;
        }
        setActiveNoteVerse(verse);
        setNoteContent('');
    };

    const saveNote = () => {
        if (!noteContent.trim()) {
            toast.error('Note cannot be empty');
            return;
        }

        addNote({
            type: 'quran',
            reference: `${surah.name} ${surah.number}:${activeNoteVerse.number}`,
            content: noteContent,
        });

        toast.success('Note saved successfully!');
        setActiveNoteVerse(null);
        setNoteContent('');
    };

    const handleCopy = (verse, includeTranslation = false) => {
        const text = includeTranslation
            ? `${verse.arabic}\n\n${verse.translation}\n\nQuran ${surah.name}:${verse.number}`
            : `${verse.arabic}\n\nQuran ${surah.name}:${verse.number}`;

        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const handleShare = (verse) => {
        setActiveShareVerse(verse);
    };

    const shareToPlatform = (platform) => {
        if (!activeShareVerse) return;

        const text = `Read Surah ${surah.name}, Ayat ${activeShareVerse.number}:\n\n${activeShareVerse.arabic}\n\n${activeShareVerse.translation}`;
        const url = window.location.href;
        const encodedText = encodeURIComponent(text);
        const encodedUrl = encodeURIComponent(url);

        let shareUrl = '';

        switch (platform) {
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
                break;
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodeURIComponent(`Quran ${surah.name}:${activeShareVerse.number}`)}&summary=${encodedText}`;
                break;
            case 'whatsapp':
                shareUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
                break;
            default:
                break;
        }

        if (shareUrl) {
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
        }
        setActiveShareVerse(null);
    };

    const currentFontClass = fontClasses[user?.preferences?.arabicFont] || 'font-arabic';
    const isRtlTranslation = ['urdu', 'arabic', 'farsi'].includes(selectedTranslation);

    if (loading) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 flex flex-col items-center justify-center min-h-[400px] h-full">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                    <SafeIcon icon={FiLoader} className="w-10 h-10 text-emerald-600 mb-4" />
                </motion.div>
                <p className="text-gray-600 dark:text-gray-300">Loading Surah...</p>
            </div>
        );
    }

    const isRead = user?.progress?.quran?.read?.includes(surah.number);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-full flex flex-col relative overflow-hidden">
            {/* Header */}
            <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800 z-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {surah.name} - {surah.arabicName}
                            {isRead && <SafeIcon icon={FiCheckCircle} className="text-emerald-500 w-5 h-5" title="Read" />}
                        </h2>
                        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300">
                            {surah.meaning} • {surah.verses} verses • {surah.type}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 self-end md:self-auto">
                        <button
                            onClick={() => user ? setShowPlaylistModal(true) : toast.error('Please sign in')}
                            className="p-1.5 md:p-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 transition-colors border border-gray-200 dark:border-gray-600 rounded-lg"
                            title="Add to Playlist"
                        >
                            <SafeIcon icon={FiList} className="w-4 h-4" />
                        </button>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={onPlayToggle}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                        >
                            <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="w-4 h-4" />
                            <span>{isPlaying ? 'Pause' : 'Play'}</span>
                        </motion.button>
                        <a
                            href={`https://download.quranicaudio.com/quran/${activeReciter}/${String(surah.number).padStart(3, '0')}.mp3`}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 md:p-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 transition-colors border border-gray-200 dark:border-gray-600 rounded-lg"
                        >
                            <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        </a>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm bg-gray-50 dark:bg-gray-750 p-2 rounded-lg">
                    <select
                        value={activeReciter}
                        onChange={(e) => setActiveReciter(e.target.value)}
                        className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-emerald-500 outline-none flex-1 md:flex-none md:max-w-[150px]"
                    >
                        {Array.isArray(config?.reciters) &&
                            config.reciters.map(reciter => (
                                <option key={reciter.id} value={reciter.id}>
                                    {reciter.name}
                                </option>
                            ))
                        }
                    </select>

                    <select
                        value={selectedTranslation}
                        onChange={(e) => setSelectedTranslation(e.target.value)}
                        className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-emerald-500 outline-none flex-1 md:flex-none"
                    >
                        <option value="arabic">Arabic Only</option>
                        <option value="bangla">Bangla</option>
                        <option value="chinese">Chinese</option>
                        <option value="english">English</option>
                        <option value="farsi">Farsi (Persian)</option>
                        <option value="french">French</option>
                        <option value="hindi">Hindi</option>
                        <option value="indonesian">Indonesian</option>
                        <option value="japanese">Japanese</option>
                        <option value="russian">Russian</option>
                        <option value="spanish">Spanish</option>
                        <option value="turkish">Turkish</option>
                        <option value="urdu">Urdu</option>
                    </select>

                    <select
                        value={fontSize}
                        onChange={(e) => setFontSize(e.target.value)}
                        className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-emerald-500 outline-none"
                    >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                        <option value="xlarge">X-Large</option>
                    </select>

                    <button
                        onClick={handleMarkAsRead}
                        disabled={isRead}
                        className={`ml-auto text-xs px-2 py-1 md:px-3 md:py-1 rounded-full border transition-colors ${isRead ? 'bg-emerald-100 text-emerald-700 border-emerald-200 cursor-default' : 'bg-white hover:bg-gray-100 text-gray-600 border-gray-300'}`}
                    >
                        {isRead ? 'Read' : 'Mark Read'}
                    </button>
                </div>
            </div>

            {/* Verses List */}
            <div className="overflow-y-auto p-3 md:p-6 space-y-2 flex-1 custom-scrollbar">
                {verses.map((verse) => (
                    <motion.div
                        key={verse.number}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="group relative pb-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 rounded-lg px-2 -mx-2 transition-colors"
                    >
                        {/* Verse Number & Actions */}
                        <div className="flex items-center justify-between mb-1">
                            <div className="w-6 h-6 md:w-8 md:h-8 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full flex items-center justify-center text-xs md:text-sm font-bold border border-emerald-200 dark:border-emerald-800 shrink-0">
                                {verse.number}
                            </div>

                            {/* Hover Menu */}
                            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white dark:bg-gray-800 shadow-sm rounded-full px-2 py-1 border border-gray-100 dark:border-gray-700 scale-90 origin-right">
                                <button onClick={() => openNoteModal(verse)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 hover:text-blue-600 transition-all" title="Add Note"><SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleFavorite(verse)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 hover:text-red-500 transition-all" title="Favorite"><SafeIcon icon={FiHeart} className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleCopy(verse)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 hover:text-indigo-500 transition-all" title="Copy"><SafeIcon icon={FiCopy} className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleShare(verse)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 hover:text-purple-500 transition-all" title="Share"><SafeIcon icon={FiShare2} className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>

                        {/* Arabic Text */}
                        <div className={`text-right mb-1 leading-loose text-gray-900 dark:text-white ${fontSizeClasses[fontSize]} ${currentFontClass}`} dir="rtl">
                            {verse.arabic}
                        </div>

                        {/* Translation */}
                        {selectedTranslation !== 'arabic' && (
                            <div className={`text-gray-600 dark:text-gray-300 leading-snug ${isRtlTranslation ? 'text-right font-arabic text-lg' : 'text-left text-base'}`} dir={isRtlTranslation ? 'rtl' : 'ltr'}>
                                {verse.translation}
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>

            {/* Modals - Updated with fixed positioning to fix clipping/grey-out issues */}
            <AnimatePresence>
                {activeNoteVerse && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-gray-200 dark:border-gray-700"
                        >
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-750">
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                    Add Note to Verse {activeNoteVerse.number}
                                </h3>
                                <button
                                    onClick={() => setActiveNoteVerse(null)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <SafeIcon icon={FiX} className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-4">
                                <p className="text-xs text-gray-500 mb-2 truncate font-arabic text-right dir-rtl">
                                    {activeNoteVerse.arabic}
                                </p>
                                <textarea
                                    value={noteContent}
                                    onChange={(e) => setNoteContent(e.target.value)}
                                    placeholder="Type your reflection or note here..."
                                    className="w-full h-32 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none resize-none text-gray-900 dark:text-white"
                                    autoFocus
                                />
                            </div>

                            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2 bg-gray-50 dark:bg-gray-750">
                                <button
                                    onClick={() => setActiveNoteVerse(null)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveNote}
                                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                                    Save Note
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {activeShareVerse && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm overflow-hidden border border-gray-200 dark:border-gray-700"
                        >
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-750">
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                    Share Ayat
                                </h3>
                                <button
                                    onClick={() => setActiveShareVerse(null)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <SafeIcon icon={FiX} className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => shareToPlatform('whatsapp')}
                                    className="flex flex-col items-center justify-center p-4 gap-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                                        <SafeIcon icon={FiShare2} className="w-5 h-5" />
                                    </div>
                                    <span className="text-sm font-medium">WhatsApp</span>
                                </button>

                                <button
                                    onClick={() => shareToPlatform('twitter')}
                                    className="flex flex-col items-center justify-center p-4 gap-2 bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                                        <SafeIcon icon={FiTwitter} className="w-5 h-5" />
                                    </div>
                                    <span className="text-sm font-medium">Twitter</span>
                                </button>

                                <button
                                    onClick={() => shareToPlatform('facebook')}
                                    className="flex flex-col items-center justify-center p-4 gap-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center">
                                        <SafeIcon icon={FiFacebook} className="w-5 h-5" />
                                    </div>
                                    <span className="text-sm font-medium">Facebook</span>
                                </button>

                                <button
                                    onClick={() => {
                                        handleCopy(activeShareVerse, true);
                                        setActiveShareVerse(null);
                                    }}
                                    className="flex flex-col items-center justify-center p-4 gap-2 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                        <SafeIcon icon={FiLink} className="w-5 h-5" />
                                    </div>
                                    <span className="text-sm font-medium">Copy Link</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showPlaylistModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm overflow-hidden border border-gray-200 dark:border-gray-700"
                        >
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-750">
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                    Add to Playlist
                                </h3>
                                <button
                                    onClick={() => setShowPlaylistModal(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <SafeIcon icon={FiX} className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-4">
                                <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                                    {(user?.playlists || []).length === 0 ? (
                                        <p className="text-sm text-gray-500 text-center py-2">No playlists created yet.</p>
                                    ) : (
                                        user.playlists.map(playlist => (
                                            <button
                                                key={playlist.id}
                                                onClick={() => handleAddToPlaylist(playlist.id)}
                                                className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex justify-between items-center transition-colors"
                                            >
                                                <span className="font-medium text-gray-900 dark:text-white">{playlist.name}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">{playlist.surahs.length} surahs</span>
                                            </button>
                                        ))
                                    )}
                                </div>

                                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-gray-500 mb-2">Create New Playlist</p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newPlaylistName}
                                            onChange={(e) => setNewPlaylistName(e.target.value)}
                                            maxLength={15}
                                            placeholder="Playlist Name"
                                            className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                        <button
                                            onClick={handleCreateAndAddPlaylist}
                                            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                                        >
                                            <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default QuranReader;